import type { CanvasNode, NodeData, PickedReference, ReferenceTag } from '../types';
import { inferReferenceKind } from './referenceMedia';
import type { ReferenceChoice } from './workspaceEdit';

/**
 * Reference nodes an Image or Video node uses without a connection. Like a connection, a
 * pick follows its Reference node: the node's current media and name are used, and a pick
 * whose node is gone is dropped. They reach the node in the same lists as connected media,
 * after it, under ids that tell them apart from connection ids, so removing one takes it
 * off the node instead of deleting a connection.
 */
const PREFIX = 'picked:';

export const pickedInputId = (reference: Pick<PickedReference, 'id'>): string => `${PREFIX}${reference.id}`;
export const isPickedInputId = (id: string): boolean => id.startsWith(PREFIX);

/** The node's list without the reference behind this input id. */
export const withoutPicked = (picked: PickedReference[] | undefined, inputId: string): PickedReference[] =>
  (picked || []).filter(reference => pickedInputId(reference) !== inputId);

export type FindNode = (id: string) => CanvasNode | undefined;

/**
 * A Reference node in another workspace of the project: the node, null when that workspace
 * has been read and no longer has it, or undefined when it has not been read (yet).
 */
export type FindShared = (workspaceId: string, nodeId: string) => CanvasNode | null | undefined;

interface ResolvedPick {
  inputId: string;
  kind: PickedReference['kind'];
  url: string;
  label: string;
  /** The Reference node's tag (Character, Background…), for models that treat them differently. */
  tag?: ReferenceTag;
  durationSec?: number;
  sizeBytes?: number;
}

/** A pick as its Reference node is now. */
const fromNode = (inputId: string, reference: PickedReference, source: NodeData): ResolvedPick[] => {
  const kind = inferReferenceKind(source);
  if (!kind) return [];
  const label = source.title || reference.label;
  if (kind === 'image') return [{ inputId, kind, url: source.imageUrl!, label, tag: source.referenceTag }];
  if (kind === 'video') return [{ inputId, kind, url: source.videoUri!, label, durationSec: source.videoDurationSec, sizeBytes: source.videoSizeBytes }];
  return [{ inputId, kind, url: source.audioUrl!, label, durationSec: source.audioDurationSec, sizeBytes: source.audioSizeBytes }];
};

/** A pick as it was when picked (picks by address, and other workspaces' nodes not read yet). */
const fromSnapshot = (inputId: string, reference: PickedReference): ResolvedPick[] =>
  (reference.url ? [{ inputId, kind: reference.kind, url: reference.url, label: reference.label, tag: reference.tag, durationSec: reference.durationSec, sizeBytes: reference.sizeBytes }] : []);

/** What each pick stands for now. */
const resolvePicks = (data: NodeData, findNode: FindNode, findShared?: FindShared): ResolvedPick[] =>
  (data.pickedReferences || []).flatMap((reference): ResolvedPick[] => {
    const inputId = pickedInputId(reference);
    if (!reference.nodeId) return fromSnapshot(inputId, reference);
    if (reference.workspaceId) {
      const shared = findShared?.(reference.workspaceId, reference.nodeId);
      if (shared === null) return []; // its workspace no longer has it
      return shared ? fromNode(inputId, reference, shared.data) : fromSnapshot(inputId, reference);
    }
    const source = findNode(reference.nodeId)?.data;
    return source ? fromNode(inputId, reference, source) : [];
  });

/** Picks of one kind that are not also connected (connected media is used once, as connected). */
const ofKind = (data: NodeData, kind: PickedReference['kind'], connectedUrls: string[], findNode: FindNode, findShared?: FindShared) =>
  resolvePicks(data, findNode, findShared).filter(pick => pick.kind === kind && !connectedUrls.includes(pick.url));

export const pickedImageInputs = (data: NodeData, connectedUrls: string[], findNode: FindNode, findShared?: FindShared) =>
  ofKind(data, 'image', connectedUrls, findNode, findShared).map(pick => ({ id: pick.inputId, url: pick.url, label: pick.label, tag: pick.tag }));

export const pickedAudioInputs = (data: NodeData, connectedUrls: string[], findNode: FindNode, findShared?: FindShared) =>
  ofKind(data, 'audio', connectedUrls, findNode, findShared).map(pick => ({
    id: pick.inputId, url: pick.url, label: pick.label, durationSec: pick.durationSec, sizeBytes: pick.sizeBytes,
  }));

/** Picked clips; their trim ranges are kept under the same id as a connected clip's under its node. */
export const pickedVideoInputs = (data: NodeData, connectedUrls: string[], findNode: FindNode, findShared?: FindShared) =>
  ofKind(data, 'video', connectedUrls, findNode, findShared).map(pick => ({ id: pick.inputId, sourceId: pick.inputId, url: pick.url, label: pick.label }));

/** The list id a pick has there: its Reference node (here or in another workspace), or its address. */
export const pickChoiceId = (reference: PickedReference): string =>
  reference.nodeId
    ? (reference.workspaceId ? `shared:${reference.workspaceId}:${reference.nodeId}` : `node:${reference.nodeId}`)
    : `url:${reference.url}`;

/** The list ids of what the node uses now, to tick them in the list. */
export const pickedChoiceIds = (picked: PickedReference[] | undefined): string[] => (picked || []).map(pickChoiceId);

/** The id a choice is ticked under, the same way a pick of it is. */
const choiceKey = (choice: ReferenceChoice): string =>
  choice.nodeId
    ? (choice.workspaceId ? `shared:${choice.workspaceId}:${choice.nodeId}` : `node:${choice.nodeId}`)
    : `url:${choice.url}`;

/**
 * The picks that still stand for something in the list: picks by address, picks whose
 * Reference node is offered, and picks from a workspace that has not been read yet.
 */
export const livePicks = (picked: PickedReference[], choices: ReferenceChoice[], readWorkspaces: ReadonlySet<string>): PickedReference[] => {
  const offered = new Set(choices.map(choiceKey));
  return picked.filter(reference => !reference.nodeId
    || offered.has(pickChoiceId(reference))
    || (!!reference.workspaceId && !readWorkspaces.has(reference.workspaceId)));
};

let counter = 0;
const newId = () => `${Date.now().toString(36)}-${(counter++).toString(36)}`;

/**
 * Adds the Reference node, or takes it off again when the node already uses it. A Reference
 * node from another workspace is linked, not copied: the pick names it, and keeps what it
 * holds now for until that workspace is read.
 */
export const togglePicked = (picked: PickedReference[] | undefined, choice: ReferenceChoice): PickedReference[] => {
  const list = picked || [];
  const key = choiceKey(choice);
  const same = (reference: PickedReference) => pickChoiceId(reference) === key;
  if (list.some(same)) return list.filter(reference => !same(reference));
  if (choice.nodeId && choice.workspaceId) {
    return [...list, {
      id: newId(), kind: choice.kind, nodeId: choice.nodeId, workspaceId: choice.workspaceId,
      // A short, lasting address only: media held inline (a browser-only workspace's) would be a copy
      ...(/^data:/i.test(choice.url) ? {} : { url: choice.url }),
      label: choice.label,
      ...(choice.tag ? { tag: choice.tag } : {}),
      ...(choice.mimeType ? { mimeType: choice.mimeType } : {}),
      ...(choice.durationSec ? { durationSec: choice.durationSec } : {}),
      ...(choice.sizeBytes ? { sizeBytes: choice.sizeBytes } : {}),
    }];
  }
  return [...list, choice.nodeId
    ? { id: newId(), kind: choice.kind, nodeId: choice.nodeId, label: choice.label }
    : { id: newId(), kind: choice.kind, url: choice.url, label: choice.label }];
};
